import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
  useReactFlow,
  ReactFlowProvider,
  getNodesBounds,
  getViewportForBounds,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FaUserTie, FaExpand, FaCompress, FaDownload, FaCaretDown, FaCaretRight, FaBriefcase, FaExclamationCircle, FaTimes, FaUsers, FaCalendarAlt } from 'react-icons/fa';
import { toPng, toJpeg } from 'html-to-image';
import axiosInstance from '../utils/axiosConfig';
import './OrganizationChart.css';
import dagre from 'dagre';
import logoSura from './logo sura color.png';

// Paleta amplia de colores pastel, 20 diferentes
const departmentColors = [
  '#f9e2ae', '#abdbe3', '#d2bbf6', '#b6e2d3', '#ffd1dc', '#c2ecef', // 6
  '#fae6b2', '#ffb4a2', '#b3c6e4', '#e9d5ff', '#a5d8ff', '#ffe382', // 12
  '#f6bfbf', '#bcf5bc', '#e0c3fc', '#b1ede8', '#fff5ba', '#aeddcd', // 18
  '#c8e6fa', '#ffe9b1'
];
const getDepartmentColor = (dep) => {
  if (!dep) return { bg: '#f9fafb', border: '#d1d5db' };
  let hash = 0;
  for (let i = 0; i < dep.length; i++) {
    hash = dep.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % departmentColors.length;
  return { bg: departmentColors[idx], border: departmentColors[idx] };
};

// Paleta de color único por gerencia
const GERENCIA_BASE_COLOR = {
  "Supply Chain": "#5b98df",   // azul
  "Comercial": "#de4242",   // rojo
  "Finanzas": "#f7c948",   // dorado/amarillo
  "IT": "#34c759",   // verde
  "Sin Gerencia": "#ececec"    // neutro para fallback
};
function getGerenciaColor(gerencia) {
  return GERENCIA_BASE_COLOR[gerencia] || GERENCIA_BASE_COLOR['Sin Gerencia'];
}

// Funcion misma que los tags badge-cell
const stringToColor = (str) => {
  if (!str) return { bg: '#f3f4f6', border: '#d1d5db', text: '#4b5563' };
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return {
    bg: `hsl(${hue}, 70%, 92%)`,
    border: `hsl(${hue}, 60%, 75%)`,
    text: `hsl(${hue}, 50%, 35%)`
  };
};

// Función para calcular tiempo en la empresa
const calcularTiempoEnEmpresa = (fechaIngreso) => {
  if (!fechaIngreso) return null;

  // Parsear fecha en varios formatos posibles
  let fecha;
  const fechaStr = String(fechaIngreso).trim();

  // Intentar diferentes formatos
  if (fechaStr.includes('/')) {
    // Formato DD/MM/YYYY
    const parts = fechaStr.split('/');
    if (parts.length === 3) {
      const [dia, mes, anio] = parts;
      fecha = new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));
    }
  } else if (fechaStr.includes('-')) {
    // Formato YYYY-MM-DD o DD-MM-YYYY
    const parts = fechaStr.split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        fecha = new Date(fechaStr);
      } else {
        // DD-MM-YYYY
        const [dia, mes, anio] = parts;
        fecha = new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));
      }
    }
  } else {
    // Intentar parseo directo
    fecha = new Date(fechaStr);
  }

  if (!fecha || isNaN(fecha.getTime())) return null;

  const hoy = new Date();
  const diffMs = hoy - fecha;
  const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDias < 0) return null;

  const anios = Math.floor(diffDias / 365);
  const meses = Math.floor((diffDias % 365) / 30);

  if (anios >= 1) {
    if (meses > 0) {
      return `${anios}a ${meses}m`;
    }
    return `${anios} año${anios !== 1 ? 's' : ''}`;
  } else if (meses >= 1) {
    return `${meses} mes${meses !== 1 ? 'es' : ''}`;
  } else {
    return diffDias <= 7 ? 'Nuevo' : `${diffDias}d`;
  }
};

// Componente para vacantes
const VacanteNode = ({ data, highlight }) => {
  // Determinar color del badge según prioridad
  const prioridadColors = {
    'Alta': '#ef4444',
    'Media': '#f59e0b',
    'Baja': '#10b981'
  };
  const prioridadColor = prioridadColors[data.prioridad] || '#6b7280';

  return (
    <div
      className={"org-chart-node org-chart-node-vacante" + (highlight ? " org-chart-node-highlight" : "")}
      style={{
        position: 'relative',
        background: '#2d3748',
        borderColor: '#4a5568',
        borderStyle: 'dashed',
        borderWidth: '3px',
        color: '#ffffff'
      }}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      <div className="node-header">
        <FaBriefcase className="node-icon" style={{ color: '#cbd5e0' }} />
        {data.prioridad && (
          <div
            className="vacante-badge"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: prioridadColor,
              color: 'white',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: 'bold'
            }}
          >
            {data.prioridad}
          </div>
        )}
      </div>
      <div className="node-body">
        <div className="node-name" style={{ fontStyle: 'italic', color: '#e2e8f0' }}>
          <FaExclamationCircle style={{ marginRight: '4px', fontSize: '12px', color: '#f59e0b' }} />
          VACANTE
        </div>
        <div className="node-cargo" style={{ color: '#ffffff' }}>
          {data.cargo || 'Sin cargo'}
        </div>
        {data.nombre && data.nombre !== 'VACANTE' && (
          <div className="node-detalles-cargo" style={{ fontSize: '11px', color: '#cbd5e0' }}>
            {data.nombre}
          </div>
        )}
      </div>
      <div className="node-footer">
        {data.fecha_creacion && (
          <div style={{ fontSize: '10px', color: '#a0aec0', textAlign: 'center' }}>
            Creada: {new Date(data.fecha_creacion).toLocaleDateString()}
          </div>
        )}
        {data.subordinados_count > 0 && (
          <div className="node-subordinates" style={{ color: '#cbd5e0' }}>
            {data.subordinados_count} subordinado{data.subordinados_count !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para empleados
const EmployeeNode = ({ data, highlight, isInPath }) => {
  const fotoUrl = data.foto || null;
  const gerenciaLabel = data.gerencia || 'Sin Gerencia';
  const colorStyle = stringToColor(gerenciaLabel);
  const tiempoEnEmpresa = calcularTiempoEnEmpresa(data.fecha_ingreso);

  // Calcular subordinados directos e indirectos
  const subordinadosDirectos = data.subordinados?.length || 0;
  const subordinadosTotales = data.subordinados_count || subordinadosDirectos;

  // Debug para fecha de ingreso si no se calcula
  if (data.fecha_ingreso && !tiempoEnEmpresa) {
    console.debug(`Fallo al calcular tiempo para ${data.nombre}:`, data.fecha_ingreso);
  }

  return (
    <div
      className={"org-chart-node" + (highlight ? " org-chart-node-highlight" : "") + (isInPath ? " org-chart-node-in-path" : "")}
      style={{
        position: 'relative',
        background: colorStyle.bg,
        borderColor: isInPath ? '#3b82f6' : colorStyle.border,
        color: '#111827',
        boxShadow: isInPath ? '0 0 0 3px rgba(59, 130, 246, 0.3)' : undefined
      }}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      <div className="node-header">
        {fotoUrl ? (
          <img src={fotoUrl} alt={data.nombre} className="node-photo" />
        ) : (
          <FaUserTie className="node-icon" />
        )}
      </div>
      <div className="node-body">
        <div className="node-name">{data.nombre}</div>
        <div className="node-cargo">{data.cargo || 'Sin cargo'}</div>
        {data.detalles_cargo && (
          <div className="node-detalles-cargo">{data.detalles_cargo}</div>
        )}
      </div>
      <div className="node-footer">
        <div className="node-stats">
          {/* Subordinados */}
          <div className="node-stat-item" title={subordinadosTotales > 0 ? `${subordinadosDirectos} directo${subordinadosDirectos !== 1 ? 's' : ''}, ${subordinadosTotales} total` : 'Sin subordinados'}>
            <FaUsers className={subordinadosTotales > 0 ? 'text-blue-500' : 'text-gray-300'} />
            <span className={subordinadosTotales > 0 ? 'text-gray-700' : 'text-gray-400'}>
              {subordinadosTotales > 0 ? subordinadosTotales : '0'}
            </span>
          </div>

          {/* Tiempo en empresa */}
          {tiempoEnEmpresa && (
            <div className="node-stat-item" title={`Tiempo en la empresa: ${tiempoEnEmpresa}`}>
              <FaCalendarAlt className="text-emerald-500" />
              <span className="text-gray-700">{tiempoEnEmpresa}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  employee: EmployeeNode,
  vacante: VacanteNode,
};

// Layout automático con dagre respetando niveles jerárquicos
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';
  // IMPORTANTE: usar 'ranker' en 'network-simplex' para respetar mejor los ranks manuales
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 350, // Separación vertical entre niveles
    nodesep: 400, // Separación horizontal entre nodos (aumentada para evitar solapamiento)
    ranker: 'network-simplex', // Mejor respeto de los ranks asignados
    align: undefined // No forzar alineación, permite niveles independientes
  });

  // Tamaños reales de las tarjetas
  const nodeWidth = 320;
  const nodeHeight = 200;

  // Encontrar el nivel máximo y mínimo para calcular ranks
  const niveles = nodes.map(node => node.data.nivel).filter(n => n != null && n > 0);
  const maxNivel = niveles.length > 0 ? Math.max(...niveles) : 90;
  const minNivel = niveles.length > 0 ? Math.min(...niveles) : 10;

  console.log('Niveles encontrados:', { maxNivel, minNivel, niveles });

  nodes.forEach((node) => {
    const nivel = node.data.nivel;

    // Configurar el nodo con su tamaño
    const nodeConfig = { width: nodeWidth, height: nodeHeight };

    // Si el empleado tiene un nivel definido, asignarlo como rank para forzar el posicionamiento vertical
    // nivel 10 (Presidente) → rank 0 (arriba)
    // nivel 80 (Recepcionista) → rank más alto (abajo)
    if (nivel != null && nivel > 0) {
      // Usar el nivel directamente como rank (10, 20, 30, etc.)
      nodeConfig.rank = nivel;
      console.log(`Nodo ${node.data.nombre}: nivel ${nivel} → rank ${nodeConfig.rank}`);
    } else {
      // Si no tiene nivel, ponerlo al final
      nodeConfig.rank = maxNivel + 100;
      console.log(`Nodo ${node.data.nombre}: sin nivel → rank ${nodeConfig.rank}`);
    }

    dagreGraph.setNode(node.id, nodeConfig);
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  // Crear mapas de padres a hijos (separando directos de secundarios)
  const parentToChildren = {}; // Solo subordinados directos
  const parentToSecondaryChildren = {}; // Solo subordinados secundarios
  const childToParents = {}; // Para detectar subordinados compartidos

  edges.forEach(edge => {
    const isSecondary = edge.id && edge.id.startsWith('e-sec-');

    if (isSecondary) {
      // Es supervisor secundario
      if (!parentToSecondaryChildren[edge.source]) {
        parentToSecondaryChildren[edge.source] = [];
      }
      parentToSecondaryChildren[edge.source].push(edge.target);
    } else {
      // Es supervisor directo
      if (!parentToChildren[edge.source]) {
        parentToChildren[edge.source] = [];
      }
      parentToChildren[edge.source].push(edge.target);
    }

    // Registrar todos los padres de cada hijo
    if (!childToParents[edge.target]) {
      childToParents[edge.target] = [];
    }
    childToParents[edge.target].push(edge.source);
  });

  // Identificar grupos de supervisores que comparten subordinados
  const supervisorGroups = new Map(); // Map<supervisorId, groupId>
  let groupIdCounter = 0;

  Object.keys(parentToChildren).forEach(parentId => {
    const children = parentToChildren[parentId] || [];

    // Verificar si algún hijo tiene múltiples supervisores
    children.forEach(childId => {
      const allParents = childToParents[childId] || [];
      if (allParents.length > 1) {
        // Este hijo tiene múltiples supervisores, agruparlos
        const existingGroupId = allParents.map(p => supervisorGroups.get(p)).find(g => g !== undefined);

        if (existingGroupId !== undefined) {
          // Ya existe un grupo, agregar todos los padres a ese grupo
          allParents.forEach(p => supervisorGroups.set(p, existingGroupId));
        } else {
          // Crear un nuevo grupo
          const newGroupId = `group-${groupIdCounter++}`;
          allParents.forEach(p => supervisorGroups.set(p, newGroupId));
        }
      }
    });
  });

  // Identificar nodos raíz (sin padres)
  const childNodesSet = new Set(edges.map(e => e.target));
  const rootNodes = nodes.filter(n => !childNodesSet.has(n.id));

  // Función recursiva para calcular el ancho de un subárbol
  const calculateSubtreeWidth = (nodeId, processedGroups = new Set()) => {
    const children = parentToChildren[nodeId] || [];

    // Si este nodo pertenece a un grupo de supervisores
    const groupId = supervisorGroups.get(nodeId);
    if (groupId && processedGroups.has(groupId)) {
      // Ya procesamos este grupo, devolver 0 para no contar duplicado
      return 0;
    }

    if (children.length === 0) {
      return nodeWidth;
    }

    // Marcar este grupo como procesado
    if (groupId) {
      processedGroups.add(groupId);

      // Si es un grupo, necesitamos calcular el ancho del grupo + sus hijos
      const groupMembers = [];
      supervisorGroups.forEach((gId, supId) => {
        if (gId === groupId) {
          groupMembers.push(supId);
        }
      });

      const groupSpacing = 30;
      const groupNodesWidth = (groupMembers.length * nodeWidth) + ((groupMembers.length - 1) * groupSpacing);

      // Sumar el ancho de los hijos
      const childrenWidths = children.map(childId => calculateSubtreeWidth(childId, processedGroups));
      const totalChildrenWidth = childrenWidths.reduce((sum, w) => sum + w, 0);

      // El ancho del subárbol es el máximo entre el grupo de supervisores y sus hijos
      return Math.max(groupNodesWidth, totalChildrenWidth);
    }

    // Sumar el ancho de todos los subárboles de los hijos
    const childrenWidths = children.map(childId => calculateSubtreeWidth(childId, processedGroups));
    const totalChildrenWidth = childrenWidths.reduce((sum, w) => sum + w, 0);

    return Math.max(nodeWidth, totalChildrenWidth);
  };

  // Función recursiva para reposicionar un subárbol
  const repositionSubtree = (nodeId, startX, processedGroups = new Set()) => {
    const children = parentToChildren[nodeId] || [];
    const groupId = supervisorGroups.get(nodeId);

    // Si este nodo pertenece a un grupo ya procesado, solo posicionarlo sin tocar sus hijos
    if (groupId && processedGroups.has(groupId)) {
      return;
    }

    if (children.length === 0) return;

    // Marcar este grupo como procesado
    if (groupId) {
      processedGroups.add(groupId);
    }

    // Calcular el ancho total del subárbol actual
    const totalSubtreeWidth = calculateSubtreeWidth(nodeId, new Set(processedGroups));

    let currentX = startX;

    // Reposicionar hijos
    children.forEach(childId => {
      const childWidth = calculateSubtreeWidth(childId, new Set(processedGroups));
      if (childWidth > 0) {
        const childCenterX = currentX + childWidth / 2;

        // Actualizar posición X del hijo
        const childNode = dagreGraph.node(childId);
        childNode.x = childCenterX;

        // Recursivamente reposicionar el subárbol del hijo
        repositionSubtree(childId, currentX, processedGroups);

        currentX += childWidth;
      }
    });

    // Si este nodo está en un grupo, posicionar todos los supervisores del grupo juntos
    if (groupId) {
      const groupMembers = [];
      supervisorGroups.forEach((gId, supId) => {
        if (gId === groupId) {
          groupMembers.push(supId);
        }
      });

      // Posicionar los miembros del grupo uno al lado del otro (muy cerca)
      if (groupMembers.length > 1) {
        const groupSpacing = 30; // Separación pequeña entre supervisores del mismo grupo
        const groupWidth = (groupMembers.length * nodeWidth) + ((groupMembers.length - 1) * groupSpacing);

        // Centrar el grupo sobre el espacio total del subárbol
        let groupStartX = startX + (totalSubtreeWidth - groupWidth) / 2;

        groupMembers.forEach(memberId => {
          const memberNode = dagreGraph.node(memberId);
          memberNode.x = groupStartX + nodeWidth / 2;
          groupStartX += nodeWidth + groupSpacing;
        });
      }
    } else {
      // Centrar el padre sobre sus hijos (comportamiento normal)
      if (children.length > 0) {
        const firstChildX = dagreGraph.node(children[0]).x;
        const lastChildX = dagreGraph.node(children[children.length - 1]).x;
        const parentNode = dagreGraph.node(nodeId);
        parentNode.x = (firstChildX + lastChildX) / 2;
      }
    }
  };

  // Calcular anchos y reposicionar desde las raíces
  let currentTreeOffsetX = 0;
  const treeSpacing = 200; // Separación entre árboles raíz

  rootNodes.forEach(rootNode => {
    const treeWidth = calculateSubtreeWidth(rootNode.id);

    // Posicionar la raíz
    const rootNodeData = dagreGraph.node(rootNode.id);
    rootNodeData.x = currentTreeOffsetX + treeWidth / 2;

    // Reposicionar todo el subárbol
    repositionSubtree(rootNode.id, currentTreeOffsetX);

    currentTreeOffsetX += treeWidth + treeSpacing;
  });

  // Agrupar nodos por nivel para post-procesar las posiciones Y
  const nodesByLevel = {};
  nodes.forEach((node) => {
    const nivel = node.data.nivel || 0;
    if (!nodesByLevel[nivel]) {
      nodesByLevel[nivel] = [];
    }
    nodesByLevel[nivel].push(node.id);
  });

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPos = dagreGraph.node(node.id);
    const nivel = node.data.nivel;

    // FORZAR posición Y basada ÚNICAMENTE en el nivel
    // Dagre solo maneja la posición X (horizontal)
    let finalY = nodeWithPos.y - nodeHeight / 2;

    if (nivel != null && nivel > 0) {
      // Mapeo directo de nivel a posición Y en pixeles
      // Ajustamos el espacio entre Especialista (60) y Analista (70)
      const nivelToY = {
        10: 0,      // Presidente
        20: 300,    // VP
        30: 600,    // Gerente
        31: 900,   // Gerente Nacional
        32: 1200,   // Gerente Regional
        40: 1200,   // Jefe
        50: 1500,   // Coordinador
        51: 1500,   // Coordinador de Ventas
        60: 1800,   // Especialista
        70: 2100,   // Analista (reducido: solo 250px desde Especialista)
        80: 2400,   // Ayudante
        90: 2700    // Pasante
      };

      finalY = nivelToY[nivel] !== undefined ? nivelToY[nivel] : 4100;

      console.log(`Nodo ${node.data.nombre} (nivel ${nivel}) → Y=${finalY}px`);
    } else {
      // Sin nivel, al final
      finalY = 4100;
    }

    return {
      ...node,
      position: {
        x: nodeWithPos.x - nodeWidth / 2,
        y: finalY,
      },
    };
  });

  return layoutedNodes;
};

function OrganizationChartInner({ departamentoId, gerenciaId, areaId, employees = [], centeredEmployeeId, searchTerm }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredPath, setHoveredPath] = useState(new Set());
  const containerRef = useRef(null);
  const { getNodes } = useReactFlow();
  const reactFlowInstance = useReactFlow();

  // Función para encontrar el camino desde un nodo hasta la raíz
  const findPathToRoot = useCallback((nodeId) => {
    const path = new Set();
    const pathEdges = new Set();
    let currentId = nodeId;

    while (currentId) {
      path.add(currentId);
      // Buscar el edge que tiene este nodo como target
      const parentEdge = allEdges.find(e => e.target === currentId);
      if (parentEdge) {
        pathEdges.add(parentEdge.id);
        currentId = parentEdge.source;
      } else {
        break;
      }
    }

    return { nodes: path, edges: pathEdges };
  }, [allEdges]);

  // Handler para hover en nodos
  const handleNodeMouseEnter = useCallback((event, node) => {
    const path = findPathToRoot(node.id);
    setHoveredPath(path.nodes);

    // Actualizar estilos de edges para resaltar el camino
    setEdges(currentEdges =>
      currentEdges.map(edge => ({
        ...edge,
        className: path.edges.has(edge.id) ? 'highlighted' : '',
        animated: path.edges.has(edge.id),
        zIndex: path.edges.has(edge.id) ? 1000 : 1
      }))
    );
  }, [findPathToRoot, setEdges]);

  const handleNodeMouseLeave = useCallback(() => {
    setHoveredPath(new Set());

    // Restaurar estilos originales de edges
    setEdges(currentEdges =>
      currentEdges.map(edge => ({
        ...edge,
        className: '',
        animated: false,
        zIndex: 1
      }))
    );
  }, [setEdges]);

  // Para resaltar el nodo, usamos una versión memoizada de nodeTypes
  const memoNodeTypes = useMemo(() => ({
    employee: (props) => (
      <EmployeeNode
        {...props}
        highlight={String(centeredEmployeeId) === props.id}
        isInPath={hoveredPath.has(props.id)}
      />
    ),
    vacante: (props) => (
      <VacanteNode
        {...props}
        highlight={String(centeredEmployeeId) === props.id}
      />
    ),
  }), [centeredEmployeeId, hoveredPath]);

  useEffect(() => {
    fetchHierarchy();
  }, [departamentoId, gerenciaId, areaId]);

  // Detectar cambios en el modo pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Centrar y resaltar empleado buscado si hay uno seleccionado
  useEffect(() => {
    if (centeredEmployeeId) {
      // Buscar el nodo con el id correspondiente
      const node = nodes.find(n => n.id === String(centeredEmployeeId));
      if (node && reactFlowInstance && reactFlowInstance.fitView) {
        const viewPadding = 80;
        reactFlowInstance.fitView({ nodes: [node], padding: 0.2 });
      }
    }
  }, [centeredEmployeeId, nodes, reactFlowInstance]);

  // Función para enfocar en un nodo (doble click)
  const handleNodeDoubleClick = useCallback((event, node) => {
    if (focusedNodeId === node.id) {
      // Si ya estamos enfocados en este nodo, volver a la vista completa
      setFocusedNodeId(null);
      setNodes(allNodes);
      setEdges(allEdges);
      setTimeout(() => {
        reactFlowInstance?.fitView({ padding: 0.2 });
      }, 50);
    } else {
      // Enfocar en este nodo y sus subordinados
      const nodeId = node.id;
      const subordinateIds = new Set([nodeId]);

      // Función recursiva para encontrar todos los subordinados
      const findSubordinates = (id) => {
        allEdges.forEach(edge => {
          if (edge.source === id && !subordinateIds.has(edge.target)) {
            subordinateIds.add(edge.target);
            findSubordinates(edge.target);
          }
        });
      };

      findSubordinates(nodeId);

      // Filtrar nodos y edges
      const filteredNodes = allNodes.filter(n => subordinateIds.has(n.id));
      const filteredEdges = allEdges.filter(e => subordinateIds.has(e.source) && subordinateIds.has(e.target));

      setFocusedNodeId(nodeId);
      setNodes(filteredNodes);
      setEdges(filteredEdges);

      // Centrar la vista en el nodo enfocado
      setTimeout(() => {
        reactFlowInstance?.fitView({ padding: 0.2 });
      }, 50);
    }
  }, [focusedNodeId, allNodes, allEdges, reactFlowInstance, setNodes, setEdges]);

  // Función para alternar pantalla completa
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error al cambiar modo pantalla completa:', err);
    }
  };

  // Función para descargar el organigrama como imagen de ultra alta calidad
  const downloadImage = async () => {
    if (!containerRef.current) return;

    setIsExporting(true);

    try {
      console.log('=== INICIANDO EXPORTACIÓN ===');

      // Obtener el elemento del ReactFlow
      const reactFlowElement = containerRef.current.querySelector('.react-flow__viewport');

      if (!reactFlowElement) {
        throw new Error('No se pudo encontrar el elemento del organigrama');
      }
      console.log('✓ Elemento ReactFlow encontrado');

      // Obtener los bounds REALES de todos los nodos (contenido efectivo)
      const nodes = getNodes();

      if (nodes.length === 0) {
        throw new Error('No hay nodos para exportar');
      }
      console.log(`✓ ${nodes.length} nodos encontrados`);

      // Calcular el bounding box exacto de todos los nodos
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      nodes.forEach(node => {
        const nodeWidth = node.width || 220; // Ancho por defecto del nodo
        const nodeHeight = node.height || 200; // Alto por defecto del nodo

        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + nodeWidth);
        maxY = Math.max(maxY, node.position.y + nodeHeight);
      });

      // Calcular dimensiones del contenido real (sin espacios blancos extras)
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;

      const padding = 100; // Padding razonable alrededor del organigrama
      const imageWidth = contentWidth + (padding * 2);
      const imageHeight = contentHeight + (padding * 2);

      console.log('✓ Dimensiones calculadas:', {
        minX, minY, maxX, maxY,
        contentWidth, contentHeight,
        imageWidth, imageHeight
      });

      // Calcular el pixelRatio óptimo basado en las dimensiones REALES
      // Límite máximo del canvas: 16384px (límite de Chrome/Firefox)
      const maxCanvasSize = 16384;
      let optimalPixelRatio = 4; // Empezamos con 4x para máxima calidad

      // Ajustar el pixelRatio para no exceder los límites del canvas
      if (imageWidth * optimalPixelRatio > maxCanvasSize || imageHeight * optimalPixelRatio > maxCanvasSize) {
        optimalPixelRatio = Math.min(
          Math.floor(maxCanvasSize / imageWidth),
          Math.floor(maxCanvasSize / imageHeight),
          4 // Máximo 4x
        );
      }

      // Asegurar que sea al menos 2x para mantener calidad
      optimalPixelRatio = Math.max(optimalPixelRatio, 2);

      const finalWidth = imageWidth * optimalPixelRatio;
      const finalHeight = imageHeight * optimalPixelRatio;

      console.log('✓ Pixel ratio óptimo:', optimalPixelRatio + 'x');
      console.log('✓ Resolución final:', finalWidth, 'x', finalHeight, 'px');

      // PASO 1: Capturar el organigrama primero
      console.log('--- PASO 1: Capturando organigrama ---');
      const chartOptions = {
        backgroundColor: '#ffffff',
        width: imageWidth,
        height: imageHeight,
        style: {
          width: imageWidth + 'px',
          height: imageHeight + 'px',
          transform: `translate(${-minX + padding}px, ${-minY + padding}px)`,
        },
        quality: 1.0,
        pixelRatio: optimalPixelRatio,
        cacheBust: true,
        skipFonts: false,
      };

      // Capturar el organigrama
      let chartDataUrl;
      try {
        console.log('Intentando primera captura del organigrama...');
        chartDataUrl = await toPng(reactFlowElement, chartOptions);
        console.log('Primera captura completada, tamaño:', chartDataUrl.length);

        if (chartDataUrl.length < 1000) {
          throw new Error('Imagen del organigrama está vacía');
        }

        // Segunda pasada para asegurar calidad
        await new Promise(resolve => setTimeout(resolve, 150));
        console.log('Intentando segunda captura del organigrama...');
        chartDataUrl = await toPng(reactFlowElement, chartOptions);
        console.log('Segunda captura completada, tamaño:', chartDataUrl.length);

        if (chartDataUrl.length < 1000) {
          throw new Error('Imagen del organigrama está vacía en segunda captura');
        }

      } catch (firstError) {
        console.warn('⚠ Primera captura del organigrama falló:', firstError);
        console.log('Reintentando con pixel ratio reducido...');

        const fallbackOptions = {
          ...chartOptions,
          pixelRatio: 2,
        };

        await new Promise(resolve => setTimeout(resolve, 300));
        chartDataUrl = await toPng(reactFlowElement, fallbackOptions);
        console.log('Captura de respaldo completada, tamaño:', chartDataUrl.length);

        if (chartDataUrl.length < 1000) {
          throw new Error('No se pudo generar la imagen del organigrama correctamente');
        }
      }

      console.log('✓ PASO 1 COMPLETADO: Organigrama capturado exitosamente');

      // PASO 2: Combinar con encabezado usando Canvas API
      console.log('--- PASO 2: Creando canvas con encabezado (alta calidad) ---');

      const headerHeight = 200;
      const finalImageWidth = Math.max(imageWidth, 1200);
      const finalImageHeight = imageHeight + headerHeight;

      // Factor de escala para mantener alta calidad (sin comprimir)
      const scaleFactor = 2;
      const canvasWidth = finalImageWidth * scaleFactor;
      const canvasHeight = finalImageHeight * scaleFactor;

      console.log('Dimensiones canvas (alta resolución):', {
        canvasWidth,
        canvasHeight,
        scaleFactor,
        'dimensiones lógicas': { finalImageWidth, finalImageHeight }
      });

      // Crear canvas de alta resolución
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');

      // Escalar todo el contexto
      ctx.scale(scaleFactor, scaleFactor);

      // Fondo blanco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalImageWidth, finalImageHeight);

      // Dibujar gradiente del encabezado
      const gradient = ctx.createLinearGradient(0, 0, finalImageWidth, 0);
      gradient.addColorStop(0, '#f8f9fa');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, finalImageWidth, headerHeight - 3);

      // Línea azul inferior del encabezado
      ctx.fillStyle = '#0066cc';
      ctx.fillRect(0, headerHeight - 3, finalImageWidth, 3);

      console.log('✓ Encabezado dibujado en canvas');

      // Cargar y dibujar el logo
      console.log('Cargando logo...');
      const logoImg = new Image();
      logoImg.src = logoSura;

      let logoWidth = 0;
      let logoX = 0;

      await new Promise((resolve, reject) => {
        logoImg.onload = () => {
          console.log('✓ Logo cargado, dimensiones originales:', logoImg.width, 'x', logoImg.height);
          // Dibujar logo (100px de altura, mantener aspecto)
          const logoHeight = 100;
          logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          logoX = 50;
          const logoY = (headerHeight - 3 - logoHeight) / 2;

          ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
          console.log('✓ Logo dibujado en canvas, ancho:', logoWidth);
          resolve();
        };
        logoImg.onerror = () => {
          console.error('❌ Error al cargar el logo');
          reject(new Error('No se pudo cargar el logo'));
        };
      });

      // Dibujar texto del encabezado - A LA DERECHA del logo con margen
      const textStartX = logoX + logoWidth + 40; // 40px de margen después del logo
      console.log('Posición del texto X:', textStartX);

      // Detectar inteligentemente qué se está capturando
      let orgTitle = 'Estructura Organizativa';
      let orgSubtitle = '';
      let orgSubtitle2 = ''; // Segunda línea de subtítulo si es necesario

      // PRIORIDAD 1: Verificar si hay un empleado enfocado (doble clic)
      if (focusedNodeId && nodes.length > 0) {
        // Buscar el nodo enfocado
        const focusedNode = nodes.find(n => n.id === focusedNodeId);

        if (focusedNode && focusedNode.data) {
          const empleado = focusedNode.data;
          const cargo = empleado.cargo || 'Sin cargo';

          if (empleado.tipo === 'vacante') {
            orgSubtitle = `${cargo} (Vacante)`;
            console.log('📍 Capturando VACANTE enfocada:', cargo);
          } else {
            // Solo mostrar cargo, NO el nombre del empleado
            orgSubtitle = `${cargo}`;
            console.log('📍 Capturando empleado enfocado:', empleado.nombre, '-', cargo);

            // Agregar info de gerencia/área en la misma línea
            if (empleado.gerencia) {
              orgSubtitle += ` - ${empleado.gerencia}`;
            }
            if (empleado.area && empleado.area !== empleado.gerencia) {
              orgSubtitle += ` / ${empleado.area}`;
            }
          }
        }
      }
      // PRIORIDAD 2: Filtros de gerencia/área/departamento
      else if (nodes.length > 0) {
        const firstNode = nodes[0].data;

        // Prioridad: Área > Gerencia > Departamento
        if (areaId && firstNode.area) {
          orgTitle = 'Estructura Organizativa';
          orgSubtitle = `Área de ${firstNode.area}`;
          console.log('📍 Capturando área:', firstNode.area);
        } else if (gerenciaId && firstNode.gerencia) {
          orgTitle = 'Estructura Organizativa';
          orgSubtitle = `Gerencia de ${firstNode.gerencia}`;
          console.log('📍 Capturando gerencia:', firstNode.gerencia);
        } else if (departamentoId && firstNode.departamento) {
          orgTitle = 'Estructura Organizativa';
          orgSubtitle = `Departamento de ${firstNode.departamento}`;
          console.log('📍 Capturando departamento:', firstNode.departamento);
        }
      }

      // Título principal
      ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#1a1a1a';
      ctx.fillText(orgTitle, textStartX, 60);

      // Subtítulos (si hay empleado enfocado o filtro específico)
      let currentY = 95;
      if (orgSubtitle) {
        ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#0066cc';
        ctx.fillText(orgSubtitle, textStartX, currentY);
        currentY += 30; // Espacio para la siguiente línea
      }

      if (orgSubtitle2) {
        ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.fillText(orgSubtitle2, textStartX, currentY);
        currentY += 30;
      }

      // Descripción
      const currentDate = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Ajustar posición de descripción según cuántos subtítulos hay
      const descriptionY = orgSubtitle ? (orgSubtitle2 ? currentY + 5 : 125) : 100;

      ctx.font = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#555555';
      ctx.fillText(`Generado el ${currentDate}`, textStartX, descriptionY);

      ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#777777';
      ctx.fillText(`Total de posiciones: ${nodes.length}`, textStartX, descriptionY + 25);

      console.log('✓ Texto del encabezado dibujado (título inteligente):');
      console.log('  Título:', orgTitle);
      console.log('  Subtítulo 1:', orgSubtitle || '(ninguno)');
      console.log('  Subtítulo 2:', orgSubtitle2 || '(ninguno)');

      // Cargar y dibujar la imagen del organigrama
      console.log('Cargando imagen del organigrama...');
      const chartImg = new Image();
      chartImg.src = chartDataUrl;

      await new Promise((resolve, reject) => {
        chartImg.onload = () => {
          console.log('✓ Imagen del organigrama cargada');
          // Centrar el organigrama horizontalmente
          const chartX = (finalImageWidth - imageWidth) / 2;
          const chartY = headerHeight;

          ctx.drawImage(chartImg, chartX, chartY, imageWidth, imageHeight);
          console.log('✓ Organigrama dibujado en canvas');
          resolve();
        };
        chartImg.onerror = () => {
          console.error('❌ Error al cargar la imagen del organigrama');
          reject(new Error('No se pudo cargar la imagen del organigrama'));
        };
      });

      console.log('✓ PASO 2 COMPLETADO: Canvas creado con éxito (alta resolución)');

      // PASO 3: Exportar el canvas como PNG
      console.log('--- PASO 3: Exportando canvas a PNG ---');

      const finalDataUrl = canvas.toDataURL('image/png', 1.0);
      console.log('✓ Canvas exportado, tamaño:', finalDataUrl.length);

      if (finalDataUrl.length < 1000) {
        throw new Error('La imagen final está vacía');
      }

      // Descargar la imagen final
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      const fileSize = Math.round((finalDataUrl.length * 0.75) / 1024);
      link.download = `estructura-organizativa-sura-${timestamp}.png`;
      link.href = finalDataUrl;
      link.click();

      console.log(`✓ EXPORTACIÓN COMPLETADA (~${fileSize}KB)`);
      console.log('=== FIN DE EXPORTACIÓN ===');

    } catch (err) {
      console.error('❌ Error al exportar la imagen:', err);
      console.error('Stack:', err.stack);
      alert(`Hubo un error al exportar la imagen: ${err.message}\nRevisa la consola para más detalles.`);
    } finally {
      setIsExporting(false);
    }
  };

  // Modificar fetchHierarchy para soportar colapso
  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (departamentoId) params.append('departamento_id', departamentoId);
      if (gerenciaId) params.append('gerencia_id', gerenciaId);
      if (areaId) params.append('area_id', areaId);
      const response = await axiosInstance.get(`/api/empleados/jerarquia?${params}`);
      const data = response.data;
      if (!data.success) throw new Error(data.error || 'Error al cargar la jerarquía');
      if (!data.jerarquia || data.jerarquia.length === 0) {
        setNodes([]); setEdges([]);
        setError('No hay datos de jerarquía para mostrar. Asegúrate de configurar los supervisores.');
        setLoading(false); return;
      }
      // --- Procesamiento soportando colapso ---
      const newNodes = [];
      const newEdges = [];
      // Para el esquema de colores
      const areasPorGerencia = {};
      data.jerarquia.forEach((e) => {
        const collectAreas = (emp) => {
          const gerenciaKey = emp.gerencia || 'Sin Gerencia';
          if (!areasPorGerencia[gerenciaKey]) areasPorGerencia[gerenciaKey] = [];
          if (emp.area && !areasPorGerencia[gerenciaKey].includes(emp.area)) {
            areasPorGerencia[gerenciaKey].push(emp.area);
          }
          (emp.subordinados || []).forEach(collectAreas);
        };
        collectAreas(e);
      });

      const processEmployee = (employee, parentCollapsed = false) => {
        const currCollapsed = false;

        // Determinar el tipo de nodo basándose en el campo 'tipo' del backend
        const nodeType = employee.tipo === 'vacante' ? 'vacante' : 'employee';

        newNodes.push({
          id: String(employee.id),
          type: nodeType,
          data: {
            ...employee,
            _allAreasForGerencia: areasPorGerencia[employee.gerencia] || [],
            hasSub: (employee.subordinados?.length > 0),
            collapsed: currCollapsed,
            toggleCollapse: undefined
          },
          position: { x: 0, y: 0 },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });

        // Si el padre está colapsado, no agrego hijos ni edges
        if (parentCollapsed || currCollapsed) return;

        // Procesar subordinados directos
        if (employee.subordinados && employee.subordinados.length > 0) {
          employee.subordinados.forEach(subordinado => {
            // Estilo diferente para conexiones con vacantes
            const edgeStyle = subordinado.tipo === 'vacante'
              ? { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5,5' }
              : { stroke: '#9ca3af', strokeWidth: 2 };

            newEdges.push({
              id: `e-${employee.id}-${subordinado.id}`,
              source: String(employee.id),
              target: String(subordinado.id),
              type: 'step',
              style: edgeStyle,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: subordinado.tipo === 'vacante' ? '#f59e0b' : '#9ca3af'
              },
            });
            processEmployee(subordinado);
          });
        }

        // Procesar subordinados secundarios (relación punteada)
        if (employee.subordinados_secundarios && employee.subordinados_secundarios.length > 0) {
          employee.subordinados_secundarios.forEach(subordinado => {
            // Estilo con línea punteada para indicar supervisor secundario
            const edgeStyle = {
              stroke: '#3b82f6',
              strokeWidth: 2,
              strokeDasharray: '8,4'
            };

            newEdges.push({
              id: `e-sec-${employee.id}-${subordinado.id}`,
              source: String(employee.id),
              target: String(subordinado.id),
              type: 'step',
              style: edgeStyle,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#3b82f6'
              },
              animated: true, // Animación para distinguir visualmente
            });
            // No procesar recursivamente los secundarios para evitar duplicados
          });
        }
      };
      data.jerarquia.forEach(employee => {
        processEmployee(employee);
      });
      // Aplicar layout automático para distribuir los nodos
      const layoutedNodes = getLayoutedElements(newNodes, newEdges, 'TB');
      const processedEdges = newEdges.map(e => ({ ...e, type: 'step' }));

      // Guardar los nodos y edges originales para el zoom
      setAllNodes(layoutedNodes);
      setAllEdges(processedEdges);

      // Si no hay un nodo enfocado, mostrar todo
      if (!focusedNodeId) {
        setNodes(layoutedNodes);
        setEdges(processedEdges);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) return <div className="loading-message">Cargando jerarquía...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="org-chart-container" ref={containerRef}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={memoNodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onMove={(event, viewport) => setZoomLevel(viewport.zoom)}
        fitView
        minZoom={0.1}
        defaultEdgeOptions={{
          type: 'step',
          style: { stroke: '#9ca3af', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#9ca3af' }
        }}
        onInit={(instance) => {
          instance.fitView();
          setZoomLevel(instance.getViewport().zoom);
        }}
      >
        <Controls />
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>

      {/* Indicador de Zoom */}
      <div className="zoom-indicator">
        {Math.round(zoomLevel * 100)}%
      </div>
      <div className="org-chart-actions">
        {focusedNodeId && (
          <button
            onClick={() => {
              setFocusedNodeId(null);
              setNodes(allNodes);
              setEdges(allEdges);
            }}
            className="org-chart-action-btn org-chart-focus-btn"
            title="Volver a vista completa"
          >
            <FaTimes />
            <span>Vista completa</span>
          </button>
        )}
        <button onClick={toggleFullscreen} className="org-chart-action-btn">
          {isFullscreen ? <FaCompress /> : <FaExpand />}
        </button>
        <button onClick={downloadImage} className="org-chart-action-btn" disabled={isExporting}>
          {isExporting ? 'Exportando...' : <FaDownload />}
        </button>
      </div>
    </div>
  );
}

function OrganizationChart(props) {
  return (
    <ReactFlowProvider>
      <OrganizationChartInner {...props} />
    </ReactFlowProvider>
  );
}

export default OrganizationChart;
